import { Skeleton } from "@cmp-src/primitives/skeleton";
import React, { useRef, useState, useEffect, ReactNode } from "react";
import { ScrollArea, ScrollAreaProps } from "@cmp-src/primitives/scroll-area";
import { cn } from "@gettactic/helpers";
import { isTacticAppWebView } from "@gettactic/helpers/src/isTacticAppWebView";

interface VirtualizedListProps {
  children: ReactNode;
  itemHeight: number;
  height?: number;
  isLoading?: boolean;
  scrollAreaProps?: ScrollAreaProps;
  gap?: number;
}

export const VirtualizedList: React.FC<VirtualizedListProps> = ({
  children,
  itemHeight,
  height,
  isLoading = false,
  scrollAreaProps,
  gap = 8
}) => {
  const isMobileApp = isTacticAppWebView();

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [visibleHeight, setVisibleHeight] = useState<number | undefined>(
    height
  );

  const itemsArray = React.Children.toArray(children);
  const totalHeight =
    itemsArray.length * itemHeight + Math.max(0, itemsArray.length - 1) * gap;

  const effectiveHeight = visibleHeight ?? 0;
  const visibleCount = Math.ceil(effectiveHeight / (itemHeight + gap));
  const overscanCount = Math.ceil(visibleCount / 2);
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / (itemHeight + gap)) - overscanCount
  );
  const endIndex = Math.min(
    itemsArray.length,
    startIndex + visibleCount + overscanCount * 2
  );
  const visibleItems = itemsArray.slice(startIndex, endIndex);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => setScrollTop(el.scrollTop);
    el.addEventListener("scroll", handleScroll);

    if (height === undefined) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setVisibleHeight(entry.contentRect.height);
        }
      });
      observer.observe(el);

      return () => {
        el.removeEventListener("scroll", handleScroll);
        observer.disconnect();
      };
    }
    return () => {
      el.removeEventListener("scroll", handleScroll);
    };
  }, [height]);

  const content = (
    <>
      {isLoading ? (
        <div>
          {Array.from({
            length: Math.ceil(effectiveHeight / (itemHeight + gap))
          }).map((_, i) => (
            <Skeleton
              // biome-ignore lint/suspicious/noArrayIndexKey:
              key={i}
              className="w-full"
              style={{
                height: itemHeight,
                marginBottom: gap
              }}
            />
          ))}
        </div>
      ) : (
        <div style={{ height: totalHeight, position: "relative" }}>
          {visibleItems.map((item, index) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey:
              key={startIndex + index}
              style={{
                height: itemHeight,
                position: "absolute",
                top: (startIndex + index) * (itemHeight + gap),
                width: "100%"
              }}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (isMobileApp) {
    return (
      <div
        className={cn(
          "virtualized-list w-full overflow-auto",
          "[-webkit-overflow-scrolling:touch]",
          "[transform:translateZ(0)]",
          "[backface-visibility:hidden]",
          "[contain:paint]",
          scrollAreaProps?.className
        )}
        ref={scrollRef}
      >
        {content}
      </div>
    );
  }

  return (
    <ScrollArea
      {...scrollAreaProps}
      className={cn(
        "w-full [&>div>div]:!min-w-0 [&>div>div]:!block",
        scrollAreaProps?.className
      )}
      viewportRef={scrollRef}
    >
      {content}
    </ScrollArea>
  );
};
